import React, { useState } from "react";
import PageShell from "@/components/PageShell";
import { FadeUp, RevealLines } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import RequestDmfDialog from "@/components/RequestDmfDialog";
import { FileDown } from "lucide-react";

const pipeline = [
  {
    id: "NGP-107",
    cls: "Phenethylamine",
    stage: "Preclinical safety-pharm",
    stagePct: 0.35,
    indication: "ADHD · Cognitive fatigue",
    partner: "CRO — Allschwil",
  },
  {
    id: "NGP-214",
    cls: "Tropane analog",
    stage: "Discovery",
    stagePct: 0.15,
    indication: "Narcolepsy",
    partner: "In-house",
  },
  {
    id: "NGP-330",
    cls: "Eugeroic (modafinil-class)",
    stage: "Phase I (partner)",
    stagePct: 0.55,
    indication: "Shift-work sleep disorder",
    partner: "EU Consortium",
  },
  {
    id: "NGP-402",
    cls: "Modafinil analog",
    stage: "IND-enabling",
    stagePct: 0.42,
    indication: "Treatment-resistant depression",
    partner: "In-house",
  },
  {
    id: "NGP-518",
    cls: "2-Aminoindane",
    stage: "Discovery",
    stagePct: 0.12,
    indication: "Cognitive decline (aging)",
    partner: "ETH Zürich (grant)",
  },
  {
    id: "NGP-621",
    cls: "Cathinone chemotype",
    stage: "Preclinical",
    stagePct: 0.28,
    indication: "Binge-eating disorder",
    partner: "In-house",
  },
];

const compoundClasses = [
  {
    tag: "I",
    name: "Phenethylamines",
    formula: "R-C6H4-CH2-CH(NH2)-CH3",
    body:
      "Our phenethylamine program pursues selective monoamine releasers with reduced peripheral sympathomimetic burden. NGP-107 introduces an α-methyl substitution stabilising the aminergic backbone against MAO-A while preserving DAT/NET selectivity in HEK-293 uptake assays (IC50 = 118 ± 12 nM for DAT, > 4,200 nM for SERT).",
    moa: "Substrate-type releaser at DAT/NET; negligible SERT activity.",
  },
  {
    tag: "II",
    name: "Tropane analogs",
    formula: "8-methyl-8-azabicyclo[3.2.1]octane core",
    body:
      "Bicyclic tropane frameworks act as high-affinity DAT reuptake inhibitors. Program NGP-214 modifies the 3β-aryl ester to shift binding kinetics toward slow-onset, atypical DAT inhibition — a profile hypothesised to lower abuse liability while retaining wakefulness and executive-function benefits.",
    moa: "Atypical DAT inhibition · slow onset · low reinforcement.",
  },
  {
    tag: "III",
    name: "Eugeroics",
    formula: "Diphenylmethyl-sulfinyl-acetamide scaffolds",
    body:
      "Modafinil-class eugeroics remain among the safest CNS wake-promoting agents in clinical use. Our NGP-330 and NGP-402 series introduce fluorinated aromatic substitutions to improve oral bioavailability (Papp > 22 × 10⁻⁶ cm/s, Caco-2) and increase half-life without shifting the receptor engagement profile.",
    moa: "Weak DAT inhibition · orexinergic and histaminergic modulation.",
  },
  {
    tag: "IV",
    name: "2-Aminoindanes",
    formula: "2,3-dihydro-1H-inden-2-amine",
    body:
      "The 2-aminoindane scaffold offers a conformationally restricted phenethylamine geometry with attenuated neurotoxicity in dopaminergic long-term cultures. NGP-518 explores 5,6-methylenedioxy analogs for cognition indications in an ETH Zürich collaborative grant.",
    moa: "Balanced NE/DA release · low serotonergic footprint.",
  },
];

const abstracts = [
  {
    id: "ABS-001",
    title: "Selective DAT/NET releasing activity of α-methylated phenethylamines in a modified HEK-293 uptake assay.",
    authors: "Weber M., Rohner K., Amstad L., Béguelin P.",
    journal: "NewGenPharma Preclinical Reports · Vol. II · 2024",
    body:
      "We characterise a series of eleven α-methyl-substituted phenethylamines (NGP-107-a to k) in an in vitro monoamine uptake assay using HEK-293 cells stably transfected with human DAT, NET, or SERT. Compounds were profiled for IC50 potency, cytotoxicity (MTS, 48 h) and selectivity ratios. Lead compound NGP-107 exhibited IC50(DAT) = 118 ± 12 nM, IC50(NET) = 89 ± 9 nM and IC50(SERT) > 4,200 nM, establishing a > 35-fold selectivity window over the serotonergic system. In parallel MAO-A microsomal assays, α-methylation reduced MAO-mediated turnover by 71 % relative to reference d-amphetamine, consistent with the hypothesised backbone stabilisation.",
  },
  {
    id: "ABS-002",
    title: "Fluorinated modafinil analogs with improved Caco-2 permeability and hepatic microsomal stability.",
    authors: "Béguelin P., Iselin V., Rohner K.",
    journal: "NewGenPharma Preclinical Reports · Vol. II · 2024",
    body:
      "A focused library of eighteen fluorinated diphenylmethyl-sulfinyl-acetamide analogs (NGP-330/402 series) was evaluated for Caco-2 apparent permeability, human liver microsomal stability (HLM t½), and P-gp efflux liability. Fluorination at the ortho-position of both aromatic rings improved Papp from 14.1 to 22.7 × 10⁻⁶ cm/s and extended HLM t½ from 46 min to > 120 min. Efflux ratios remained within acceptable industry ranges (BA:AB < 2.1). These properties support once-daily oral dosing in a shift-work sleep disorder indication.",
  },
  {
    id: "ABS-003",
    title: "Process-scale synthesis of a tropane 3β-aryl ester API intermediate under continuous-flow conditions.",
    authors: "Amstad L., Frei D., Sutter M.",
    journal: "NewGenPharma Process Chemistry Notes · No. 07 · 2025",
    body:
      "We report a continuous-flow variant of the classical Robinson tropinone condensation adapted to the synthesis of the NGP-214 intermediate 3β-(4-chlorophenyl)-tropane-2β-carboxylic acid methyl ester. Operating at 82 °C with a residence time of 9.4 min, the flow process delivers 92.3 % isolated yield at kilogram scale with an enantiomeric excess of 99.1 %. ICH-Q3D residual metals are within Class 2A limits (Pd < 8 ppm, Ni < 12 ppm). A full DMF-ready dossier is available under NDA.",
  },
];

export default function WhatWeDo() {
  const [hover, setHover] = useState(null);

  return (
    <PageShell title="What We Do — NewGenPharma">
      {/* HEADER */}
      <section className="px-6 md:px-10 pt-16 md:pt-24 pb-24">
        <div className="mx-auto max-w-[1600px]">
          <FadeUp>
            <div className="font-mono-tight text-[11px] text-ink/60 flex items-center gap-4 mb-8">
              <span className="w-1.5 h-1.5 bg-oxred inline-block" />
              §02 · What We Do
            </div>
          </FadeUp>
          <RevealLines
            as="h1"
            className="font-display text-[14vw] md:text-[9vw] leading-[0.92] tracking-[-0.03em]"
            lines={["Research in", <>central-nervous <em className="italic text-oxred">stimulants</em>,</>, "manufactured as API intermediates."]}
          />
          <FadeUp delay={0.5}>
            <div className="mt-12 grid md:grid-cols-3 gap-8 border-t border-ink/15 pt-10 max-w-5xl">
              <p className="text-ink/75 leading-relaxed text-sm">
                We operate at three tightly coupled altitudes: discovery of novel CNS-active scaffolds,
                preclinical characterisation of lead candidates, and process-scale manufacturing of the
                intermediate compounds our partners require.
              </p>
              <p className="text-ink/75 leading-relaxed text-sm">
                Every internal program targets a stimulant, eugeroic or cognition-enhancing profile with
                explicit deprioritisation of high abuse-liability chemotypes. Our screening cascade
                emphasises atypical DAT engagement, selectivity indices and cardiovascular safety.
              </p>
              <p className="text-ink/75 leading-relaxed text-sm">
                On the commercial side, we ship GMP-aligned API intermediates against Drug Master Files,
                with full CoA, ICH-Q3A/D residuals, and process capability data (Cp/Cpk) for every batch.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* PIPELINE TABLE */}
      <section data-testid="pipeline-section" className="px-6 md:px-10 pb-24">
        <div className="mx-auto max-w-[1600px]">
          <FadeUp>
            <div className="flex items-baseline justify-between mb-8">
              <h2 className="font-display text-4xl md:text-5xl tracking-tight">Pipeline</h2>
              <span className="font-mono-tight text-[11px] text-ink/60">
                {pipeline.length} active programs
              </span>
            </div>
          </FadeUp>

          <div className="grid grid-cols-12 font-mono-tight text-[10px] text-ink/60 py-3 border-y border-ink/15">
            <div className="col-span-2">Compound</div>
            <div className="col-span-3">Class</div>
            <div className="col-span-2">Stage</div>
            <div className="col-span-2">Indication</div>
            <div className="col-span-2 text-right">Partner</div>
            <div className="col-span-1 text-right">DMF</div>
          </div>

          <div data-testid="pipeline-table">
            {pipeline.map((r, i) => (
              <FadeUp key={r.id} delay={i * 0.05}>
                <div
                  data-testid={`pipeline-row-${r.id}`}
                  className={`grid grid-cols-12 py-8 border-b border-ink/10 items-center transition-opacity duration-300 ${
                    hover && hover !== r.id ? "opacity-30" : "opacity-100"
                  }`}
                  onMouseEnter={() => setHover(r.id)}
                  onMouseLeave={() => setHover(null)}
                >
                  <div className="col-span-2 font-mono text-sm md:text-base">{r.id}</div>
                  <div className="col-span-3 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-oxred inline-block" />
                    <span className="text-sm">{r.cls}</span>
                  </div>
                  <div className="col-span-2">
                    <div className="text-sm mb-2">{r.stage}</div>
                    <div className="h-[2px] bg-ink/10 relative w-40">
                      <div
                        className="absolute top-0 left-0 h-[2px] bg-ink"
                        style={{ width: `${r.stagePct * 100}%` }}
                      />
                    </div>
                  </div>
                  <div className="col-span-2 text-sm">{r.indication}</div>
                  <div className="col-span-2 text-right font-mono-tight text-[10px] text-ink/60">
                    {r.partner}
                  </div>
                  <div className="col-span-1 text-right">
                    <RequestDmfDialog compound={{ id: r.id, name: `${r.id} (${r.cls})` }} />
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <Marquee items={["Phenethylamines", "Tropanes", "Eugeroics", "Aminoindanes"]} />

      {/* COMPOUND CLASSES */}
      <section data-testid="classes-section" className="px-6 md:px-10 py-32">
        <div className="mx-auto max-w-[1600px]">
          <FadeUp>
            <div className="flex items-baseline justify-between mb-16 border-b border-ink/15 pb-6">
              <h2 className="font-display text-4xl md:text-5xl tracking-tight">Compound classes</h2>
              <span className="font-mono-tight text-[11px] text-ink/60">Mechanism of action</span>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/15 border border-ink/15">
            {compoundClasses.map((c) => (
              <div
                key={c.tag}
                data-testid={`class-card-${c.tag}`}
                className="bg-bone p-10 md:p-14 relative overflow-hidden group"
              >
                <FadeUp>
                  <div className="flex items-center gap-4 mb-8">
                    <span className="font-mono-tight text-[10px] text-oxred">CLASS · {c.tag}</span>
                    <span className="font-mono-tight text-[10px] text-ink/50">{c.formula}</span>
                  </div>
                  <h3 className="font-display text-4xl md:text-5xl leading-[1] tracking-tight">{c.name}</h3>
                  <p className="mt-8 text-ink/75 leading-relaxed max-w-xl text-sm md:text-base">
                    {c.body}
                  </p>
                  <div className="mt-8 pt-6 border-t border-ink/10 font-mono-tight text-[11px] text-ink/70">
                    MoA · {c.moa}
                  </div>
                </FadeUp>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABSTRACTS */}
      <section data-testid="abstracts-section" className="px-6 md:px-10 py-24 bg-bone-2/50 border-y border-ink/10">
        <div className="mx-auto max-w-[1600px]">
          <FadeUp>
            <div className="flex items-baseline justify-between mb-12 border-b border-ink/15 pb-6">
              <h2 className="font-display text-4xl md:text-5xl tracking-tight">Selected abstracts</h2>
              <span className="font-mono-tight text-[11px] text-ink/60">Internal reports · 2024–2025</span>
            </div>
          </FadeUp>

          <div className="space-y-16">
            {abstracts.map((a) => (
              <FadeUp key={a.id}>
                <article className="grid grid-cols-1 md:grid-cols-12 gap-8 pb-16 border-b border-ink/10">
                  <div className="md:col-span-3">
                    <div className="font-mono-tight text-[10px] text-oxred">{a.id}</div>
                    <div className="font-mono-tight text-[10px] text-ink/60 mt-2">{a.journal}</div>
                    <div className="font-mono-tight text-[10px] text-ink/60 mt-4 leading-relaxed">
                      {a.authors}
                    </div>
                  </div>
                  <div className="md:col-span-9">
                    <h3 className="font-display text-2xl md:text-3xl leading-[1.15] tracking-tight max-w-3xl">
                      {a.title}
                    </h3>
                    <p className="mt-6 text-ink/75 text-sm leading-relaxed max-w-3xl">{a.body}</p>
                  </div>
                </article>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* DOCUMENTATION STRIP */}
      <section className="px-6 md:px-10 py-32">
        <div className="mx-auto max-w-[1600px] grid grid-cols-1 md:grid-cols-2 gap-16">
          <FadeUp>
            <span className="font-mono-tight text-[11px] text-ink/60">§ Documentation Standards</span>
            <h3 className="font-display text-4xl md:text-6xl leading-[0.98] tracking-tight mt-6">
              Every gram, <em className="italic text-oxred">accounted for.</em>
            </h3>
            <div className="mt-10">
              <RequestDmfDialog
                compound={{ id: "GENERIC", name: "any listed compound" }}
                trigger={
                  <button
                    data-testid="request-dmf-generic"
                    className="group inline-flex items-center gap-3 px-8 py-4 bg-ink text-bone font-mono-tight text-[11px]"
                  >
                    <span className="w-1.5 h-1.5 bg-oxred inline-block" />
                    <FileDown size={14} /> Request DMF & NDA
                  </button>
                }
              />
              <div className="mt-3 font-mono-tight text-[10px] text-ink/50">
                We&apos;ll email an NDA + dossier link within seconds.
              </div>
            </div>
          </FadeUp>
          <FadeUp delay={0.15}>
            <ul className="divide-y divide-ink/10 border-y border-ink/15">
              {[
                ["CoA", "Certificate of Analysis — HPLC-UV / HPLC-MS / KF / ICP-MS"],
                ["ICH", "Q3A · Q3B · Q3C · Q3D residual profiles"],
                ["DMF", "Drug Master File-ready dossiers, U.S. FDA & EMA formats"],
                ["QMS", "ISO 9001:2015 · Swissmedic GMP alignment"],
                ["Batch", "Cp/Cpk statistical process capability with each lot"],
              ].map(([k, v]) => (
                <li key={k} className="py-5 grid grid-cols-12 gap-4">
                  <span className="col-span-2 font-mono-tight text-[11px] text-ink/60">{k}</span>
                  <span className="col-span-10 text-ink/85">{v}</span>
                </li>
              ))}
            </ul>
          </FadeUp>
        </div>
      </section>

      {/* CITATIONS / RESEARCH PORTAL */}
      <section
        data-testid="citations-section"
        id="citations"
        className="px-6 md:px-10 py-24 md:py-32 bg-bone-2/50 border-y border-ink/10"
      >
        <div className="mx-auto max-w-[1600px]">
          <FadeUp>
            <div className="flex items-baseline justify-between mb-4 flex-wrap gap-4">
              <span className="font-mono-tight text-[11px] text-ink/60">§ Research portal</span>
              <span className="font-mono-tight text-[10px] text-ink/50">
                Peer-reviewed literature grounding our programs
              </span>
            </div>
            <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[0.98] max-w-[22ch]">
              Citations &amp; <em className="italic text-oxred">further reading.</em>
            </h2>
            <p className="mt-8 max-w-2xl text-ink/75 leading-relaxed">
              The claims we make in our compound descriptions and abstracts trace back to published,
              peer-reviewed evidence. The list below is a curated starting point for anyone who wants to
              investigate our scientific positions directly. Every entry links out to the primary source.
            </p>
          </FadeUp>

          <div className="mt-16 space-y-16">
            {[
              {
                heading: "Modafinil-class eugeroics · safety and mechanism",
                refs: [
                  {
                    a: "Ballon JS, Feifel D.",
                    t: "A systematic review of modafinil: potential clinical uses and mechanisms of action.",
                    j: "J Clin Psychiatry.",
                    y: "2006",
                    v: "67(4):554-566.",
                    doi: "10.4088/jcp.v67n0406",
                    url: "https://doi.org/10.4088/jcp.v67n0406",
                  },
                  {
                    a: "Volkow ND, Fowler JS, Logan J, et al.",
                    t: "Effects of modafinil on dopamine and dopamine transporters in the male human brain: clinical implications.",
                    j: "JAMA.",
                    y: "2009",
                    v: "301(11):1148-1154.",
                    doi: "10.1001/jama.2009.351",
                    url: "https://doi.org/10.1001/jama.2009.351",
                  },
                  {
                    a: "Turner DC, Robbins TW, Clark L, et al.",
                    t: "Cognitive enhancing effects of modafinil in healthy volunteers.",
                    j: "Psychopharmacology.",
                    y: "2003",
                    v: "165(3):260-269.",
                    doi: "10.1007/s00213-002-1250-8",
                    url: "https://doi.org/10.1007/s00213-002-1250-8",
                  },
                  {
                    a: "Wisor J.",
                    t: "Modafinil as a catecholaminergic agent: empirical evidence and unanswered questions.",
                    j: "Front Neurol.",
                    y: "2013",
                    v: "4:139.",
                    doi: "10.3389/fneur.2013.00139",
                    url: "https://doi.org/10.3389/fneur.2013.00139",
                  },
                  {
                    a: "Goss AJ, Kaser M, Costafreda SG, Sahakian BJ, Fu CH.",
                    t: "Modafinil augmentation therapy in unipolar and bipolar depression: a systematic review and meta-analysis of randomized controlled trials.",
                    j: "J Clin Psychiatry.",
                    y: "2013",
                    v: "74(11):1101-1107.",
                    doi: "10.4088/JCP.13r08560",
                    url: "https://doi.org/10.4088/JCP.13r08560",
                  },
                ],
              },
              {
                heading: "Atypical DAT inhibitors · tropane pharmacology",
                refs: [
                  {
                    a: "Reith MEA, Blough BE, Hong WC, et al.",
                    t: "Behavioral, biological, and chemical perspectives on atypical agents targeting the dopamine transporter.",
                    j: "J Med Chem.",
                    y: "2015",
                    v: "58(21):8515-8531.",
                    doi: "10.1021/acs.jmedchem.5b00003",
                    url: "https://doi.org/10.1021/acs.jmedchem.5b00003",
                  },
                  {
                    a: "Loland CJ, Mereu M, Okunola OM, et al.",
                    t: "R-modafinil (armodafinil): a unique dopamine uptake inhibitor and potential medication for psychostimulant abuse.",
                    j: "Biol Psychiatry.",
                    y: "2012",
                    v: "72(5):405-413.",
                    doi: "10.1016/j.biopsych.2012.03.022",
                    url: "https://doi.org/10.1016/j.biopsych.2012.03.022",
                  },
                  {
                    a: "Newman AH, Ku T, Jordan CJ, Bonifazi A, Xi ZX.",
                    t: "New drugs, old targets: tweaking the dopamine system to treat psychostimulant use disorders.",
                    j: "Annu Rev Pharmacol Toxicol.",
                    y: "2021",
                    v: "61:609-628.",
                    doi: "10.1146/annurev-pharmtox-030220-124205",
                    url: "https://doi.org/10.1146/annurev-pharmtox-030220-124205",
                  },
                ],
              },
              {
                heading: "Phenethylamines and monoamine transporter pharmacology",
                refs: [
                  {
                    a: "Sitte HH, Freissmuth M.",
                    t: "Amphetamines, new psychoactive drugs and the monoamine transporter cycle.",
                    j: "Trends Pharmacol Sci.",
                    y: "2015",
                    v: "36(1):41-50.",
                    doi: "10.1016/j.tips.2014.11.006",
                    url: "https://doi.org/10.1016/j.tips.2014.11.006",
                  },
                  {
                    a: "Rothman RB, Baumann MH.",
                    t: "Monoamine transporters and psychostimulant drugs.",
                    j: "Eur J Pharmacol.",
                    y: "2003",
                    v: "479(1-3):23-40.",
                    doi: "10.1016/j.ejphar.2003.08.054",
                    url: "https://doi.org/10.1016/j.ejphar.2003.08.054",
                  },
                  {
                    a: "Heal DJ, Smith SL, Gosden J, Nutt DJ.",
                    t: "Amphetamine, past and present — a pharmacological and clinical perspective.",
                    j: "J Psychopharmacol.",
                    y: "2013",
                    v: "27(6):479-496.",
                    doi: "10.1177/0269881113482532",
                    url: "https://doi.org/10.1177/0269881113482532",
                  },
                ],
              },
              {
                heading: "2-Aminoindanes · conformationally-restricted phenethylamines",
                refs: [
                  {
                    a: "Nichols DE, Brewster WK, Johnson MP, Oberlender R, Riggs RM.",
                    t: "Nonneurotoxic tetralin and indan analogues of 3,4-(methylenedioxy)amphetamine.",
                    j: "J Med Chem.",
                    y: "1990",
                    v: "33(2):703-710.",
                    doi: "10.1021/jm00164a037",
                    url: "https://doi.org/10.1021/jm00164a037",
                  },
                  {
                    a: "Pinterova N, Horsley RR, Palenicek T.",
                    t: "Synthetic aminoindanes: a summary of existing knowledge.",
                    j: "Front Psychiatry.",
                    y: "2017",
                    v: "8:236.",
                    doi: "10.3389/fpsyt.2017.00236",
                    url: "https://doi.org/10.3389/fpsyt.2017.00236",
                  },
                ],
              },
              {
                heading: "Cathinones, appetite modulation and binge-eating disorder",
                refs: [
                  {
                    a: "McElroy SL, Hudson JI, Mitchell JE, et al.",
                    t: "Efficacy and safety of lisdexamfetamine for treatment of adults with moderate to severe binge-eating disorder: a randomized clinical trial.",
                    j: "JAMA Psychiatry.",
                    y: "2015",
                    v: "72(3):235-246.",
                    doi: "10.1001/jamapsychiatry.2014.2162",
                    url: "https://doi.org/10.1001/jamapsychiatry.2014.2162",
                  },
                  {
                    a: "Kelly JP.",
                    t: "Cathinone derivatives: a review of their chemistry, pharmacology and toxicology.",
                    j: "Drug Test Anal.",
                    y: "2011",
                    v: "3(7-8):439-453.",
                    doi: "10.1002/dta.313",
                    url: "https://doi.org/10.1002/dta.313",
                  },
                ],
              },
              {
                heading: "Shift-work sleep disorder and wakefulness pharmacology",
                refs: [
                  {
                    a: "Åkerstedt T, Wright KP Jr.",
                    t: "Sleep loss and fatigue in shift work and shift work disorder.",
                    j: "Sleep Med Clin.",
                    y: "2009",
                    v: "4(2):257-271.",
                    doi: "10.1016/j.jsmc.2009.03.001",
                    url: "https://doi.org/10.1016/j.jsmc.2009.03.001",
                  },
                ],
              },
              {
                heading: "Medicinal chemistry — fluorine, permeability, ADME",
                refs: [
                  {
                    a: "Purser S, Moore PR, Swallow S, Gouverneur V.",
                    t: "Fluorine in medicinal chemistry.",
                    j: "Chem Soc Rev.",
                    y: "2008",
                    v: "37(2):320-330.",
                    doi: "10.1039/b610213c",
                    url: "https://doi.org/10.1039/b610213c",
                  },
                ],
              },
            ].map((group, gi) => (
              <FadeUp key={gi} delay={gi * 0.05}>
                <div>
                  <div className="flex items-baseline gap-4 mb-6">
                    <span className="font-mono-tight text-[10px] text-oxred">
                      §{String(gi + 1).padStart(2, "0")}
                    </span>
                    <h3 className="font-display text-2xl md:text-3xl tracking-tight">
                      {group.heading}
                    </h3>
                  </div>
                  <ol className="divide-y divide-ink/10 border-y border-ink/15">
                    {group.refs.map((r, ri) => (
                      <li
                        key={ri}
                        data-testid={`citation-${gi + 1}-${ri + 1}`}
                        className="py-5 grid grid-cols-12 gap-4"
                      >
                        <span className="col-span-1 font-mono-tight text-[10px] text-ink/50 pt-1">
                          [{ri + 1}]
                        </span>
                        <div className="col-span-11">
                          <span className="text-ink/85 text-sm md:text-base leading-relaxed">
                            {r.a}{" "}
                            <span className="italic text-ink">{r.t}</span>{" "}
                            <span className="font-mono-tight text-[11px] text-ink/70">
                              {r.j}
                            </span>{" "}
                            <span className="font-mono-tight text-[11px] text-ink/70">
                              {r.y};{r.v}
                            </span>
                          </span>{" "}
                          <a
                            href={r.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 font-mono-tight text-[10px] text-oxred link-underline align-baseline"
                            data-testid={`citation-doi-${gi + 1}-${ri + 1}`}
                          >
                            doi:{r.doi} ↗
                          </a>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </FadeUp>
            ))}
          </div>

          <FadeUp>
            <div className="mt-16 pt-8 border-t border-ink/15 font-mono-tight text-[10px] text-ink/60 leading-relaxed max-w-3xl">
              This bibliography is illustrative and non-exhaustive. Additional references — including
              unpublished internal reports and DMF-supporting analytical data — are available to partners
              under a mutual NDA. Corrections or additions:{" "}
              <a
                href="mailto:press@newgenpharma.ch"
                className="text-oxred link-underline"
                data-testid="citations-corrections-email"
              >
                press@newgenpharma.ch
              </a>
              .
            </div>
          </FadeUp>
        </div>
      </section>
    </PageShell>
  );
}
